import { formatDateWithTimezone, formatMoney } from './helper';

const ACTOR_LABELS = {
  leadAgent: 'lead agent',
  client: 'client',
  admin: 'admin',
  system: 'system',
};

const ACTION_LABELS = {
  created: 'Created',
  edited: 'Edited',
  approved: 'Approved',
  declined: 'Declined',
  rolled_over: 'Rolled over',
  locked: 'Locked',
  unlocked: 'Unlocked',
  paid: 'Paid',
  payment_unchecked: 'Payment unchecked',
};

export const formatCommissionAmount = (amount) =>
  amount === null || amount === undefined ? formatMoney(0) : formatMoney(Number(amount));

export const formatHistoryTimestampUTC = formatDateWithTimezone;

const hasOwn = (obj, key) => Boolean(obj) && Object.prototype.hasOwnProperty.call(obj, key);

const formatStatusInfo = (event) => {
  const beforeStatus = event.before?.status;
  const afterStatus = event.after?.status;
  if (beforeStatus == null && afterStatus == null) return null;

  if (beforeStatus != null && afterStatus != null) {
    if (beforeStatus === afterStatus) {
      // Unchanged status is noise on edit/lock/paid — keep it only for create
      return event.action === 'created' ? `status: ${afterStatus}` : null;
    }
    return `status: ${beforeStatus} → ${afterStatus}`;
  }

  return `status: ${afterStatus ?? beforeStatus}`;
};

const formatApprovedAtInfo = (event) => {
  const hasBefore = hasOwn(event.before, 'approvedAt');
  const hasAfter = hasOwn(event.after, 'approvedAt');
  if (!hasBefore && !hasAfter) return null;

  const beforeVal = hasBefore ? event.before.approvedAt : undefined;
  const afterVal = hasAfter ? event.after.approvedAt : undefined;
  const beforeEmpty = beforeVal == null || beforeVal === '';
  const afterEmpty = afterVal == null || afterVal === '';

  // null → date (approve): show the new timestamp only
  if (beforeEmpty && !afterEmpty) {
    return `approvedAt: ${formatHistoryTimestampUTC(afterVal)}`;
  }

  // date → null (unlock): show cleared
  if (!beforeEmpty && afterEmpty && hasAfter) {
    return `approvedAt: ${formatHistoryTimestampUTC(beforeVal)} → cleared`;
  }

  // date → different date
  if (!beforeEmpty && !afterEmpty && String(beforeVal) !== String(afterVal)) {
    return `approvedAt: ${formatHistoryTimestampUTC(beforeVal)} → ${formatHistoryTimestampUTC(afterVal)}`;
  }

  // unchanged or only one side with a value
  if (!afterEmpty) {
    return event.action === 'approved' || event.action === 'created'
      ? `approvedAt: ${formatHistoryTimestampUTC(afterVal)}`
      : null;
  }

  return null;
};

const formatAmountInfo = (event) => {
  if (event.action === 'edited') {
    return `before amount: ${formatCommissionAmount(event.before?.amount)} - after amount: ${formatCommissionAmount(event.after?.amount)}`;
  }
  if (event.action === 'created') {
    return `after amount: ${formatCommissionAmount(event.after?.amount)}`;
  }
  if (event.after?.amount != null || event.before?.amount != null) {
    return `amount: ${formatCommissionAmount(event.after?.amount ?? event.before?.amount)}`;
  }
  return null;
};

export const formatHistoryEventLine = (event) => {
  if (!event) return '';

  const actionLabel = event.action
    ? ACTION_LABELS[event.action] || event.action.charAt(0).toUpperCase() + event.action.slice(1)
    : 'Unknown';
  const actorLabel = ACTOR_LABELS[event.actorRole] || event.actorRole;
  const timestamp = formatHistoryTimestampUTC(event.timestamp);

  const parts = [`${actionLabel} by ${actorLabel} - ${timestamp}`];

  const amountInfo = formatAmountInfo(event);
  if (amountInfo) parts.push(amountInfo);

  const statusInfo = formatStatusInfo(event);
  if (statusInfo) parts.push(statusInfo);

  const approvedAtInfo = formatApprovedAtInfo(event);
  if (approvedAtInfo) parts.push(approvedAtInfo);

  if (event.reason) {
    parts.push(`reason: "${event.reason}"`);
  }

  return parts.join(' - ');
};

export const getVisibleHistoryEvents = (history = [], role) => {
  const events = Array.isArray(history) ? [...history] : [];
  const filtered =
    role === 'client' ? events.filter((e) => e.action !== 'paid' && e.action !== 'payment_unchecked') : events;
  return filtered.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
};
